import { VuexModule, Module, Mutation, Action, getModule } from "vuex-module-decorators";
import { Inject } from "inversify-props";
import store from "@/core/store/index";
import { ApplicationConfigurationDto } from "@/api/model";
import type IAppConfigService from "@/core/services/AppConfigService";
import isEmpty from "lodash/isEmpty";
import { ICDUserDto, ICDOrganisationDto } from "@/core/models/CDIdentityModels";
import { discoveryService } from "@/core/services/DiscoveryService";
import i18n from "@/i18n";

export interface IAppState {
  sidebar: {
    hidden: boolean;
  };
  mode: AppMode;
  config: ApplicationConfigurationDto;
  configLoaded: boolean;
  error: string;
  cdOrganisation: ICDOrganisationDto;
  cdUser: ICDUserDto;
}

export enum AppMode {
  Loading = 0,
  Ready = 1,
  LoggingOut = 2,
  Error = 3
}

@Module({
  dynamic: true,
  store,
  name: "app"
})
class App extends VuexModule implements IAppState {
  private _appConfigService!: IAppConfigService;

  public get appConfigService(): IAppConfigService {
    return this._appConfigService;
  }

  @Inject()
  set appConfigService(service: IAppConfigService) {
    this._appConfigService = service;
  }

  public sidebar = {
    hidden: false
  };
  public config = {} as ApplicationConfigurationDto;
  public get configLoaded(): boolean {
    return !isEmpty(this.config);
  }
  public error = "";
  public mode = AppMode.Loading;

  public cdOrganisation = {} as ICDOrganisationDto;
  public cdUser = {} as ICDUserDto;

  @Mutation
  public SET_MODE(mode: AppMode) {
    this.mode = mode;
  }

  @Mutation
  public TOGGLE_SIDEBAR() {
    this.sidebar.hidden = !this.sidebar.hidden;
  }

  @Mutation
  public CLOSE_SIDEBAR() {
    this.sidebar.hidden = false;
  }

  @Mutation
  public SET_CONFIG(config: ApplicationConfigurationDto) {
    this.config = config;
  }

  @Action
  public async GetConfig() {
    try {
      await this.appConfigService
        .getApplicationConfiguration()
        .then(response => {
          this.SET_CONFIG(response.data);
          this.SET_MODE(AppMode.Ready);
          let lang = response.data.localization?.currentCulture?.englishName || "English";
          const supportedLanguages = i18n.global.messages ? Object.keys(i18n.global.messages.value) : [];
          if (!supportedLanguages.includes(lang)) {
            if (lang === "Chinese (China)" && supportedLanguages.includes("Chinese")) {
              lang = "Chinese";
            } else lang = "English";
          }
          if (lang) {
            sessionStorage.setItem("lang", lang);
            i18n.global.locale.value = lang;
          }
        })
        .catch(error => {
          console.error(error);
          this.SET_ERROR("Application Configuration Unavailable");
        });
    } catch (ex) {
      console.log("hekkiw", ex);
    }
  }

  @Mutation
  public SET_ERROR(error: string) {
    this.error = error;
    this.mode = AppMode.Error;
  }

  @Action
  public async GetCDUser(input: { idp: string; cdUserId: string }) {
    if (input.idp === "local") {
      this.SET_CD_USER({
        id: "",
        accountReference: "",
        accountType: 0,
        organisations: [],
        roles: [],
        photoBase64: "",
        isActive: false
      });
    } else if (input.idp === "AzureADB2C") {
      const data = await discoveryService.getUser(input.cdUserId);
      this.SET_CD_USER(data);
    }
  }

  @Action
  public async GetOrganisation(input: { idp: string; cdOrganisationId: string }) {
    if (input.idp === "local") {
      this.SET_CD_ORGANISATION({
        id: "",
        name: "",
        description: "",
        isActive: false,
        tenants: [],
        logoUrl: null
      });
    } else if (input.idp === "AzureADB2C") {
      const data = await discoveryService.getOrganisation(input.cdOrganisationId);
      this.SET_CD_ORGANISATION(data);
    }
  }

  @Mutation
  public SET_CD_ORGANISATION(org: ICDOrganisationDto) {
    this.cdOrganisation = org;
  }

  @Mutation
  public SET_CD_USER(cdUser: ICDUserDto) {
    this.cdUser = cdUser;
  }
}

export const AppModule = getModule(App);
